
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import {
  canPasteElement,
  canPasteElementProperties,
  copyElement,
  getElementToPasteAndPasteElement,
  pasteElementProperties
} from "@/lib/copy-paste/copy-paste";
import RightClickMenuItem from "./RightClickMenuItem.vue";
import { configStore, documentStore, uiStore } from "@/store/store";
import { getRightClickMenuPositionInCanvas } from "@/lib/right-click/actions";
import { canSaveAsGlobalBlock } from "@/lib/global-blocks/global-blocks";
import { getNodeIdAtPointInCanvas } from "@/lib/canvas/drag/element-drag";
import GlobalBlockDialog from "@/components/Reusable/GlobalBlockDialog.vue";
import ClipboardDialog from "@/components/Canvas/OverlaidUI/Canvas/ClipboardDialog.vue";
import { getClipboardStatus } from "@/lib/copy-paste/utils";
import PresetDialog from "@/components/Settings/ElementPresets/PresetDialog.vue";
import { doesElementHavePreset } from "@/lib/presets/presets";
import Icon from "@/components/UI/Icon.vue";
import RightClickWrapMenu from "@/components/Canvas/OverlaidUI/Canvas/RightClickWrapMenu.vue";
import { TreeNode } from "@/types/tree";

@Component({
  components: {
    Icon,
    PresetDialog,
    ClipboardDialog,
    GlobalBlockDialog,
    RightClickMenuItem,
    RightClickWrapMenu
  }
})
export default class RightClickMenu extends Vue {
  @Prop({ default: "canvas" })
  context!: "structure-panel" | "canvas";

  clipboardDialog = false;

  async mounted() {
    window.Breakdance.canvas.document.addEventListener(
      "contextmenu",
      this.handleRightClickInCanvas
    );

    window.Breakdance.canvas.document.addEventListener(
      "click",
      this.closeRightClickMenuIfClickHappensOutsideRightClickMenu,
      {
        capture: true
      }
    );

    window.addEventListener(
      "click",
      this.closeRightClickMenuIfClickHappensOutsideRightClickMenu,
      {
        capture: true
      }
    );
  }

  beforeDestroy() {
    window.Breakdance.canvas.document.removeEventListener(
      "contextmenu",
      this.handleRightClickInCanvas
    );

    window.Breakdance.canvas.document.removeEventListener(
      "click",
      this.closeRightClickMenuIfClickHappensOutsideRightClickMenu,
      {
        capture: true
      }
    );

    window.removeEventListener(
      "click",
      this.closeRightClickMenuIfClickHappensOutsideRightClickMenu,
      {
        capture: true
      }
    );
  }

  get menuState() {
    if (this.context === "structure-panel") {
      return uiStore.onStructurePanelRightClickMenuState !== "closed"
        ? uiStore.onStructurePanelRightClickMenuState
        : null;
    }

    return uiStore.onCanvasRightClickMenuState !== "closed"
      ? uiStore.onCanvasRightClickMenuState
      : null;
  }

  get shouldShow() {
    const isMenuOpen =
      this.context === "structure-panel"
        ? uiStore.onStructurePanelRightClickMenuState !== "closed"
        : uiStore.onCanvasRightClickMenuState !== "closed";

    return isMenuOpen && !this.saveGlobalBlockDialog;
  }

  get builderMode() {
    return configStore.builderMode;
  }

  handleRightClickInCanvas(e: MouseEvent) {
    e.preventDefault();

    const elementId = getNodeIdAtPointInCanvas(e.clientX, e.clientY);
    const position = getRightClickMenuPositionInCanvas(e.clientX, e.clientY);

    if (!position) return;

    uiStore.setOnCanvasRightClickMenuState({
      elementId,
      position
    });
  }

  closeRightClickMenuIfClickHappensOutsideRightClickMenu(e: MouseEvent) {
    if (this.saveGlobalBlockDialog) return;

    const closestMenu = (e.target as HTMLElement).closest(
      ".breakdance-canvas-right-click-menu"
    );

    if (!closestMenu) {
      this.close();
    }
  }

  get wrapperPositionStyle() {
    if (!this.menuState) return {};

    return {
      left: `${this.menuState.position.x}px`,
      top: `${this.menuState.position.y}px`
    };
  }

  get elementIdPresent() {
    return this.menuState?.elementId;
  }

  canPasteElement = false;
  canPasteElementProperties = false;
  permission?: PermissionState;

  @Watch("menuState")
  async onStateChange() {
    const { permission } = await getClipboardStatus();

    this.permission = permission?.state;

    if (this.permission === "prompt") {
      this.canPasteElement = true;
      this.canPasteElementProperties = true;
      return;
    }

    this.canPasteElement = await canPasteElement(
      this.menuState?.elementId
        ? this.menuState.elementId
        : documentStore.document.tree.root.id
    );

    this.canPasteElementProperties = this.menuState?.elementId
      ? await canPasteElementProperties(this.menuState.elementId)
      : false;
  }

  copyElement() {
    if (this.menuState?.elementId) {
      copyElement(this.menuState.elementId);
    }
    this.close();
  }

  pasteElement() {
    if (this.permission === "prompt") {
      this.clipboardDialog = true;
    }

    if (this.menuState?.elementId) {
      getElementToPasteAndPasteElement(this.menuState.elementId);
    } else {
      getElementToPasteAndPasteElement(documentStore.document.tree.root.id);
    }

    this.close();
  }

  pasteElementProperties(path: "design" | "content" | "settings") {
    if (this.permission === "prompt") {
      this.clipboardDialog = true;
    }

    if (this.menuState?.elementId) {
      pasteElementProperties(this.menuState.elementId, path);
    }

    this.close();
  }

  deleteElement() {
    if (!this.menuState?.elementId) return;

    documentStore.deleteElement({ nodeId: this.menuState?.elementId });
    this.close();
  }

  // Global Block

  elementIdToSaveAsGlobalBlock: TreeNode["id"] | null = null;
  saveGlobalBlockDialog = false;
  openSaveGlobalBlockDialog() {
    if (this.menuState?.elementId) {
      this.elementIdToSaveAsGlobalBlock = this.menuState.elementId;
      this.saveGlobalBlockDialog = true;
    }
  }

  closeGlobalBlockDialog() {
    this.saveGlobalBlockDialog = false;
    this.elementIdToSaveAsGlobalBlock = null;
    this.close();
  }

  get canSaveAsGlobalBlock() {
    return (
      this.menuState?.elementId &&
      canSaveAsGlobalBlock(this.menuState.elementId)
    );
  }

  // Preset

  elementIdToSaveAsPreset: TreeNode["id"] | null = null;
  savePresetDialog = false;
  openSavePresetDialog() {
    if (this.menuState?.elementId) {
      this.elementIdToSaveAsPreset = this.menuState.elementId;
      this.savePresetDialog = true;
    }
  }

  closePresetDialog() {
    this.savePresetDialog = false;
    this.elementIdToSaveAsPreset = null;
    this.close();
  }

  get canSaveAsPreset() {
    return !doesElementHavePreset(this.menuState?.elementId);
  }

  close() {
    uiStore.setOnCanvasRightClickMenuState("closed");
    uiStore.setOnStructurePanelRightClickMenuState("closed");
  }
}
